/**
 * Media Library (Sources → Media)
 *
 * Upload / organise / delete the tenant's sendable-media files (documents,
 * images, videos, audio), arranged into nestable folders. These are the files
 * the bot delivers to a customer on request via a "Send Media" (send_media)
 * automation node — distinct from the Files tab (RAG knowledge, never sent).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Delete,
  Folder as FolderIcon,
  CreateNewFolder,
  InsertDriveFile,
  Image as ImageIcon,
  Movie,
  Audiotrack,
  ChevronRight,
  MoreVert,
  BrokenImage,
  Visibility,
  FileDownloadOutlined,
  FolderOpen,
  EditOutlined,
} from '@mui/icons-material';
import { format } from 'date-fns';
import adminApi, {
  type MediaAsset,
  type MediaFolder,
  type MediaAssetType,
} from '@/services/admin.api';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import UsageChip from '@/components/common/UsageChip';
import MediaPreviewDialog, { type MediaPreviewKind } from '@/components/common/MediaPreviewDialog';

const ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/jpeg',
  'image/png',
  'video/mp4',
  'video/3gp',
  'audio/aac',
  'audio/mp4',
  'audio/mpeg',
  'audio/amr',
  'audio/ogg',
].join(',');

/** Type icon used as the document/audio thumbnail placeholder (types with no
 *  visual preview). Image/video render the real file, so they never use it. */
const TYPE_ICON_LARGE: Record<MediaAssetType, React.ReactNode> = {
  document: <InsertDriveFile fontSize="inherit" />,
  image: <ImageIcon fontSize="inherit" />,
  video: <Movie fontSize="inherit" />,
  audio: <Audiotrack fontSize="inherit" />,
};

/** Fixed-height preview shown at the top of every asset card. Images and
 *  videos render the actual file (same box, `object-fit: cover`, so a
 *  portrait phone photo and a widescreen video both crop to the same
 *  standard tile); documents and audio — which have no meaningful visual
 *  thumbnail — fall back to a centered type icon in an identically-sized
 *  box, so the grid stays visually uniform across every asset type. */
const THUMB_HEIGHT = 150;

/** Shared placeholder tile — a centered icon over a neutral wash, used for
 *  types with no visual thumbnail (document/audio) AND as the graceful
 *  fallback when an image or video URL is broken, expired or invalid. */
function ThumbPlaceholder({
  icon,
  label,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label?: string;
  tone?: 'default' | 'error';
}) {
  return (
    <Box
      sx={{
        height: THUMB_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        background: (t) =>
          tone === 'error'
            ? t.palette.action.hover
            : `linear-gradient(135deg, ${t.palette.action.hover} 0%, ${t.palette.action.selected} 100%)`,
        color: tone === 'error' ? 'text.disabled' : 'text.secondary',
        fontSize: 38,
        '& > svg': { fontSize: 'inherit' },
      }}
    >
      {icon}
      {label && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

function MediaThumb({ asset }: { asset: MediaAsset }) {
  // Tracks a failed load so a broken/expired/invalid URL degrades to a
  // placeholder tile instead of the browser's broken-image glyph.
  const [failed, setFailed] = useState(false);

  if (asset.type === 'image') {
    if (failed) {
      return <ThumbPlaceholder icon={<BrokenImage />} label="Preview unavailable" tone="error" />;
    }
    return (
      <Box
        component="img"
        src={asset.s3Url}
        alt={asset.name}
        loading="lazy"
        onError={() => setFailed(true)}
        sx={{
          width: '100%',
          height: THUMB_HEIGHT,
          objectFit: 'cover',
          display: 'block',
          bgcolor: 'action.hover',
        }}
      />
    );
  }
  if (asset.type === 'video') {
    if (failed) {
      return <ThumbPlaceholder icon={<BrokenImage />} label="Preview unavailable" tone="error" />;
    }
    return (
      <Box
        component="video"
        src={asset.s3Url}
        controls
        preload="metadata"
        onError={() => setFailed(true)}
        // The tile opens the preview dialog; the inline controls keep working
        // as they always have, so a scrub must not also open the modal.
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{
          width: '100%',
          height: THUMB_HEIGHT,
          objectFit: 'cover',
          display: 'block',
          bgcolor: '#000',
        }}
      />
    );
  }
  return <ThumbPlaceholder icon={TYPE_ICON_LARGE[asset.type]} />;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
}

const extractErr = (e: any): string =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Something went wrong';

const MediaSources = () => {
  // The current folder lives in the URL (?folder=<id>) so a nested folder is
  // deep-linkable and survives refresh / back-forward. `null` = Library root.
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folder');
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [path, setPath] = useState<Array<{ _id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // dialogs
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);
  /** The asset open in the in-app preview — a tile click, never a navigation. */
  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);
  const [pendingDeleteAsset, setPendingDeleteAsset] = useState<MediaAsset | null>(null);
  const [pendingDeleteFolder, setPendingDeleteFolder] = useState<MediaFolder | null>(null);

  // Edit-details dialog (name + description) for a single asset.
  const [editAsset, setEditAsset] = useState<MediaAsset | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (a: MediaAsset) => {
    setEditAsset(a);
    setEditName(a.name);
    setEditDesc(a.description ?? '');
    setError('');
  };
  const saveEdit = async () => {
    if (!editAsset || !editName.trim()) return;
    setEditSaving(true);
    setError('');
    try {
      const updated = await adminApi.updateMediaAsset(editAsset._id, {
        name: editName.trim(),
        description: editDesc.trim(),
      });
      // Sync the grid in place from the server's response — no full reload.
      setAssets((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
      setEditAsset(null);
      setSuccess('Details updated');
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setEditSaving(false);
    }
  };

  // Row actions ("⋮") menu — one menu instance, re-anchored per card. The
  // target is either an asset or a folder; never both at once.
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAsset, setMenuAsset] = useState<MediaAsset | null>(null);
  const [menuFolder, setMenuFolder] = useState<MediaFolder | null>(null);

  const openAssetMenu = (e: React.MouseEvent<HTMLElement>, a: MediaAsset) => {
    e.stopPropagation();
    setMenuFolder(null);
    setMenuAsset(a);
    setMenuAnchor(e.currentTarget);
  };
  const openFolderMenu = (e: React.MouseEvent<HTMLElement>, f: MediaFolder) => {
    e.stopPropagation();
    setMenuAsset(null);
    setMenuFolder(f);
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuAsset(null);
    setMenuFolder(null);
  };

  const load = useCallback(async (fid: string | null) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.browseMedia(fid);
      setFolders(res.folders);
      setAssets(res.assets);
      setPath(res.path);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(folderId);
  }, [folderId, load]);

  const navigateTo = (fid: string | null) => {
    // Root clears the param (back to /admin/media); a folder adds ?folder=<id>.
    // `replace: false` keeps each hop in history so the browser Back button
    // walks back up the folder tree.
    setSearchParams(fid ? { folder: fid } : {});
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await adminApi.createMediaFolder(newFolderName.trim(), folderId);
      setNewFolderOpen(false);
      setNewFolderName('');
      setSuccess('Folder created');
      await load(folderId);
    } catch (e) {
      setError(extractErr(e));
    }
  };

  const doUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setError('');
    try {
      await adminApi.uploadMediaAsset(uploadFile, {
        ...(uploadName.trim() ? { name: uploadName.trim() } : {}),
        folderId,
      });
      setUploadOpen(false);
      setUploadFile(null);
      setUploadName('');
      setSuccess('File uploaded');
      await load(folderId);
    } catch (e) {
      setError(extractErr(e));
    } finally {
      setUploading(false);
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (f) {
      setUploadFile(f);
      // Prefill the name from the filename only if the author hasn't typed one.
      if (!uploadName.trim()) setUploadName(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header — title left, the two primary actions pinned top-right. The
          actions never wrap away from the corner (flexShrink: 0 + ml:auto). */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>
          Media Library
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
          <Button variant="outlined" startIcon={<CreateNewFolder />} onClick={() => setNewFolderOpen(true)}>
            New folder
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => {
              setUploadFile(null);
              setUploadName('');
              setError('');
              setUploadOpen(true);
            }}
          >
            Upload file
          </Button>
        </Stack>
      </Box>

      {/* Toolbar — breadcrumb on the left, storage usage on the right. */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Breadcrumbs separator={<ChevronRight fontSize="small" />}>
          <Link component="button" underline="hover" color="inherit" onClick={() => navigateTo(null)}>
            Library
          </Link>
          {path.map((p, i) => (
            <Link
              key={p._id}
              component="button"
              underline="hover"
              color={i === path.length - 1 ? 'text.primary' : 'inherit'}
              onClick={() => navigateTo(p._id)}
            >
              {p.name}
            </Link>
          ))}
        </Breadcrumbs>
        <UsageChip resource="storage" label="Storage" format="bytes" />
      </Box>

      {error && !uploadOpen && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ my: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : folders.length === 0 && assets.length === 0 ? (
        <Card variant="outlined" sx={{ mt: 3, py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Empty here. Upload a file or create a folder to organise your media.
          </Typography>
        </Card>
      ) : (
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          }}
        >
          {/* Folders first */}
          {folders.map((f) => (
            <Card
              key={f._id}
              variant="outlined"
              sx={{
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'border-color .15s, box-shadow .15s, transform .15s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => navigateTo(f._id)}
            >
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                      color: 'warning.main',
                      border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.25)}`,
                    }}
                  >
                    <FolderIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography fontWeight={600} noWrap title={f.name}>
                      {f.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Folder
                    </Typography>
                  </Box>
                  <Tooltip title="Actions">
                    <IconButton
                      size="small"
                      aria-label={`Actions for folder ${f.name}`}
                      onClick={(e) => openFolderMenu(e, f)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}

          {/* Assets */}
          {assets.map((a) => (
            <Card
              key={a._id}
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'border-color .15s, box-shadow .15s, transform .15s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => setPreviewAsset(a)}
              role="button"
              tabIndex={0}
              aria-label={`Preview ${a.name}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPreviewAsset(a);
                }
              }}
            >
              {/* The type is obvious from the preview itself, so no type chip. */}
              <MediaThumb asset={a} />
              <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
                <Typography fontWeight={600} noWrap title={a.name}>
                  {a.name}
                </Typography>
                {a.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    title={a.description}
                    sx={{
                      mt: 0.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {a.description}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={a.originalName}
                  display="block"
                  sx={{ mt: 0.75 }}
                >
                  {a.originalName}
                </Typography>
                {/* Footer row — metadata on the left, actions (⋮) on the right. */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 0.75 }}
                >
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formatBytes(a.size)} · {format(new Date(a.createdAt), 'dd MMM yyyy')}
                  </Typography>
                  <Tooltip title="Actions">
                    <IconButton
                      size="small"
                      aria-label={`Actions for ${a.name}`}
                      onClick={(e) => openAssetMenu(e, a)}
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Row actions menu — shared by folder and asset cards. Delete lives
          here (never a bare trash icon on the card) alongside the primary,
          non-destructive actions. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuFolder && [
          <MenuItem
            key="open"
            onClick={() => {
              const f = menuFolder;
              closeMenu();
              if (f) navigateTo(f._id);
            }}
          >
            <ListItemIcon>
              <FolderOpen fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              setPendingDeleteFolder(menuFolder);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>,
        ]}
        {menuAsset && [
          <MenuItem
            key="preview"
            onClick={() => {
              setPreviewAsset(menuAsset);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>Preview</ListItemText>
          </MenuItem>,
          <MenuItem
            key="edit"
            onClick={() => {
              if (menuAsset) openEdit(menuAsset);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <EditOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit details</ListItemText>
          </MenuItem>,
          <MenuItem
            key="download"
            onClick={() => {
              const url = menuAsset?.s3Url;
              closeMenu();
              if (url) window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            <ListItemIcon>
              <FileDownloadOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              setPendingDeleteAsset(menuAsset);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void createFolder()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void createFolder()} disabled={!newFolderName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit details dialog — update an asset's name + description in place. */}
      <Dialog open={!!editAsset} onClose={() => !editSaving && setEditAsset(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Name"
              required
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              multiline
              rows={3}
              placeholder="What is this file / when should the bot send it?"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAsset(null)} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveEdit()}
            disabled={editSaving || !editName.trim()}
            startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog — name FIRST, then attach the file. */}
      <Dialog
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Name"
              required
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="e.g. Spring Brochure"
            />
            <Box>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                {uploadFile ? 'Change file' : 'Choose file'}
                <input hidden type="file" accept={ACCEPT} onChange={onFilePicked} />
              </Button>
              {uploadFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {uploadFile.name} · {formatBytes(uploadFile.size)}
                  {path.length > 0 && ` · into "${path[path.length - 1]?.name}"`}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void doUpload()}
            disabled={uploading || !uploadFile || !uploadName.trim()}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmations */}
      <ConfirmDialog
        open={!!pendingDeleteAsset}
        title="Delete media"
        content={`Delete "${pendingDeleteAsset?.name}"? Any automation step that sends it will stop working.`}
        confirmText="Delete"
        severity="warning"
        onCancel={() => setPendingDeleteAsset(null)}
        onConfirm={async () => {
          if (pendingDeleteAsset) {
            try {
              await adminApi.deleteMediaAsset(pendingDeleteAsset._id);
              await load(folderId);
            } catch (e) {
              setError(extractErr(e));
            }
          }
          setPendingDeleteAsset(null);
        }}
      />
      <ConfirmDialog
        open={!!pendingDeleteFolder}
        title="Delete folder"
        content={`Delete folder "${pendingDeleteFolder?.name}"? It must be empty first.`}
        confirmText="Delete"
        severity="warning"
        onCancel={() => setPendingDeleteFolder(null)}
        onConfirm={async () => {
          if (pendingDeleteFolder) {
            try {
              await adminApi.deleteMediaFolder(pendingDeleteFolder._id);
              await load(folderId);
            } catch (e) {
              setError(extractErr(e));
            }
          }
          setPendingDeleteFolder(null);
        }}
      />

      {/* In-app preview — `document` is only claimed when the mime type says
          PDF, so a .docx lands on the honest "no preview" tile instead. */}
      <MediaPreviewDialog
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        url={previewAsset?.s3Url ?? null}
        fileName={previewAsset?.originalName ?? ''}
        {...(previewAsset?.mimeType ? { mimeType: previewAsset.mimeType } : {})}
        {...(previewAsset && previewAsset.type !== 'document'
          ? { kind: previewAsset.type as MediaPreviewKind }
          : {})}
        {...(previewAsset?.description ? { caption: previewAsset.description } : {})}
      />
    </Box>
  );
};

export default MediaSources;
